import { cn } from '@/utils/cn';

/**
 * Spinner Component
 *
 * Rotating circle animation for loading states.
 * Supports multiple sizes and color customization.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <Spinner />
 *
 * // Custom size
 * <Spinner size="lg" />
 *
 * // Custom color
 * <Spinner className="border-success-500" />
 *
 * // Compact mode
 * <Spinner compact />
 *
 * // Centered in container
 * <div className="flex items-center justify-center h-64">
 *   <Spinner />
 * </div>
 * ```
 */

export interface SpinnerProps {
  /**
   * Size variant
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg' | 'xl';

  /**
   * Compact mode (extra small)
   * @default false
   */
  compact?: boolean;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Accessible label
   */
  label?: string;
}

const sizeClasses = {
  sm: 'h-4 w-4 border-2',
  md: 'h-6 w-6 border-2',
  lg: 'h-8 w-8 border-3',
  xl: 'h-12 w-12 border-4',
};

/**
 * Spinning circle loader with theme support
 */
export function Spinner({
  size = 'md',
  compact = false,
  className,
  label = 'Loading...',
}: SpinnerProps) {
  const spinnerSize = compact ? 'h-3 w-3 border-2' : sizeClasses[size];

  return (
    <div
      className={cn(
        'animate-spin rounded-full border-primary border-t-transparent',
        spinnerSize,
        className
      )}
      role="status"
      aria-label={label}
    >
      <span className="sr-only">{label}</span>
    </div>
  );
}
